import { ApiService } from './ApiService';

export interface DnsRecord {
	type: 'TXT' | 'CNAME';
	name: string;
	value: string;
}

export interface DomainDnsRecords {
	verification: DnsRecord;
	dkim: Array<DnsRecord>;
}

export interface DomainSummary {
	id: number;
	domain: string;
	verified: boolean;
	active: boolean;
	primary: boolean;
}

export interface DomainListResult {
	domains: Array<DomainSummary>;
	/**
	 * The platform address tenant emails are sent from when no tenant-owned
	 * domain is verified — i.e. the effective sender selectors should show
	 * when the tenant hasn't configured (or selected) their own domain.
	 */
	fallbackFrom: string;
}

export interface DomainWithDnsRecords {
	id: number;
	domain: string;
	verified: boolean;
	active: boolean;
	primary: boolean;
	dnsRecords: DomainDnsRecords;
	awsStatus: {
		identityVerified: boolean;
		dkimVerified: boolean;
	};
}

export interface DomainVerificationResult {
	verified: boolean;
	identityVerified: boolean;
	dkimVerified: boolean;
}

/**
 * EmailDomainService - Frontend service for email domain management
 *
 * This service provides methods for managing email sending domains:
 * - List domains for the tenant
 * - Register new domains with AWS SES
 * - Get domain details with DNS records
 * - Refresh verification status from AWS
 * - Remove domains
 */
export class EmailDomainService {
	/**
	 * List all email domains for the current tenant, along with the platform
	 * fallback from-address used when no tenant domain is verified/selected.
	 */
	public static async listDomains(): Promise<DomainListResult> {
		const response = await ApiService.apiGet<{
			success: boolean;
			data: Array<DomainSummary>;
			fallbackFrom: string;
		}>('/email/domains');

		return {
			domains: response.data,
			fallbackFrom: response.fallbackFrom,
		};
	}

	/**
	 * Register a new email domain
	 * This creates the domain in the login server and registers it with AWS SES
	 */
	static async registerDomain(domain: string): Promise<DomainWithDnsRecords> {
		const response = await ApiService.apiPost<{
			success: boolean;
			data: DomainWithDnsRecords;
		}>('/email/domains', { domain });

		return response.data;
	}

	/**
	 * Get domain details with DNS records from AWS
	 */
	static async getDomainDetails(id: number): Promise<DomainWithDnsRecords> {
		const response = await ApiService.apiGet<{
			success: boolean;
			data: DomainWithDnsRecords;
		}>(`/email/domains/${id}`);

		return response.data;
	}

	/**
	 * Refresh domain verification status from AWS
	 * This checks AWS SES for the current verification state and updates the login server
	 */
	static async refreshStatus(id: number): Promise<DomainVerificationResult> {
		const response = await ApiService.apiPost<{
			success: boolean;
			data: DomainVerificationResult;
		}>(`/email/domains/${id}/refresh`);

		return response.data;
	}

	/**
	 * Remove a domain
	 * This deletes the domain from AWS SES and the login server
	 */
	static async removeDomain(id: number): Promise<void> {
		await ApiService.apiDelete<{ success: boolean; message: string }>(
			`/email/domains/${id}`,
		);
	}

	/**
	 * Set a domain as the primary domain for the tenant
	 * The primary domain is used as the default "from" domain for tenant emails
	 */
	static async setDomainPrimary(id: number): Promise<DomainSummary> {
		const response = await ApiService.apiPut<{
			success: boolean;
			data: DomainSummary;
		}>(`/email/domains/${id}/primary`, {});

		return response.data;
	}
}
